import React, { useCallback, useState } from 'react';
import { connect } from 'react-redux';
import { Text } from '@adc/parallax-component-library';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ApiStatus } from 'Enums';
import { useAccount, useApiError } from 'Hooks';
import { Button, Form, Header, InputPassword, Main, PasswordInputValidator, ResponsiveContainer, Spinner, } from 'Components/utility';
import i18n from 'Utilities/i18n';
import { resetPass } from 'Services/auth';
const initialValues = {
    newPassword: '',
    repeatNewPassword: '',
};
const mapStateToProps = ({ config: { validation }, nav: { query: { email, token, app, country }, }, }) => {
    return {
        validation,
        email,
        token,
        app,
        leadingTrailingSpacesIdentity: validation?.identity.find((identity) => identity.data.key === 'leadingTrailingSpaces'),
        country,
    };
};
const connector = connect(mapStateToProps);
const ResetPasswordFirstStep = ({ validation, email, token, app, country, next, handleTokenExpirationError, leadingTrailingSpacesIdentity, }) => {
    const [meetsPasswordRequirements, setMeetsPasswordRequirements] = useState(false);
    const [loading, setLoading] = useState(false);
    const { showApiErrorModal } = useApiError();
    const { showPasswordValidationError } = useAccount();
    const validationSchema = Yup.object().shape({
        newPassword: leadingTrailingSpacesIdentity
            ? Yup.string().matches(new RegExp(leadingTrailingSpacesIdentity.data.regex), i18n.t(`Login.content.signInForm.formField.password.helpText.${leadingTrailingSpacesIdentity.data.key}`))
            : Yup.string(),
        repeatNewPassword: Yup.string()
            .required(i18n.t('ResetPassword.content.resetPasswordForm.formField.repeatNewPassword.errors.passwordMustMatch'))
            .oneOf([Yup.ref('newPassword')], i18n.t('ResetPassword.content.resetPasswordForm.formField.repeatNewPassword.errors.passwordMustMatch')),
    });
    const { handleSubmit, handleBlur, setFieldValue, handleChange, isValid, errors, touched, values, setTouched, setErrors, } = useFormik({
        initialValues,
        onSubmit: onSubmitForm,
        validationSchema,
        validateOnBlur: true,
    });
    const handleApiRequestError = useCallback((error) => {
        switch (error.status) {
            case ApiStatus.BAD_REQUEST: {
                const data = error.response?.data;
                if (data && data.code === ApiStatus.PASSWORD_VALIDATION) {
                    setFieldValue('newPassword', '');
                    setFieldValue('repeatNewPassword', '');
                    setTouched({});
                    setErrors({});
                    showPasswordValidationError({ validation }, data.errors, country);
                    return;
                }
                showApiErrorModal();
                break;
            }
            case ApiStatus.UNAUTHORIZED: {
                handleTokenExpirationError();
                break;
            }
            default:
                showApiErrorModal();
        }
    }, [
        showApiErrorModal,
        setFieldValue,
        setTouched,
        setErrors,
        showPasswordValidationError,
        validation,
        country,
        handleTokenExpirationError,
    ]);
    function onSubmitForm() {
        setLoading(true);
        resetPass(values.newPassword, token, app)
            .then(next)
            .catch(handleApiRequestError)
            .finally(() => {
            setLoading(false);
        });
    }
    return (React.createElement(Main, null,
        loading && React.createElement(Spinner, null),
        React.createElement("img", { src: i18n.t('Global.microcopy.common.libreLogoLight'), alt: "", id: "Global.microcopy.common.libreLogo", style: {
                position: 'absolute',
                zIndex: 1,
                marginTop: '20px',
                width: '100px',
                height: '50px',
                objectFit: 'contain',
                left: '50%',
                transform: 'translateX(-50%)',
            } }),
        React.createElement(ResponsiveContainer, null,
            React.createElement(Header, { titleTestID: "ResetPassword.title", title: i18n.t('ResetPassword.title'), textAlign: "center", titleProps: {
                    fontSize: '$h2.strong',
                    fontWeight: '$h2.strong',
                } }),
            React.createElement(Text, { marginTop: "$2", testID: "ResetPassword.subtitle", fontSize: "$bodyBase.default", fontWeight: "$bodyBase.default", color: "$text.100", textAlign: "center" }, i18n.t('ResetPassword.subtitle')),
            React.createElement(Text, { testID: "ResetPassword.title-email", fontSize: "$bodyBase.default", fontWeight: "$bodyBase.default", color: "$text.100", textAlign: "center" }, email),
            React.createElement(Form, { style: { padding: 0, marginTop: 16 } },
                React.createElement(PasswordInputValidator, { placeholder: i18n.t('ResetPassword.content.resetPasswordForm.formField.newPassword.placeholder'), label: i18n.t('ResetPassword.content.resetPasswordForm.formField.newPassword.label'), onChangeText: handleChange('newPassword'), errors: errors, touched: touched, setFieldValue: setFieldValue, handleBlur: handleBlur('newPassword'), password: values.newPassword, onValidate: setMeetsPasswordRequirements, identities: validation?.identity, testID: "newPassword" }),
                React.createElement(InputPassword, { mt: 1, mb: 5, label: i18n.t('ResetPassword.content.resetPasswordForm.formField.repeatNewPassword.label'), placeholder: i18n.t('ResetPassword.content.resetPasswordForm.formField.repeatNewPassword.placeholder'), testID: "repeatNewPassword", onBlur: handleBlur('repeatNewPassword'), setFieldValue: setFieldValue, onChangeText: handleChange('repeatNewPassword'), value: values.repeatNewPassword, isInvalid: errors.repeatNewPassword && touched.repeatNewPassword ? true : false, errorMessage: errors.repeatNewPassword }),
                React.createElement(Button, { testID: "ResetPassword.button", marginTop: "$4", onPress: () => handleSubmit(), disabled: !values.newPassword ||
                        !values.repeatNewPassword ||
                        !isValid ||
                        !meetsPasswordRequirements, label: i18n.t('ResetPassword.title'), "aria-label": i18n.t('ResetPassword.title') })))));
};
export default connector(ResetPasswordFirstStep);
